import { endOfMonth, startOfMonth, subMonths, UTCDate } from '@/utils/dates';
import { AccountView } from '../../../types/entity-types';
import { SupportedCountryCurrency } from '../../../types/supported-country-currency';
import { getMonthlyBurnData } from './get-monthly-burn-data';

type EstimatedRunwayInput = {
  accounts: AccountView[];
  baseCurrency: SupportedCountryCurrency;
  /** Start of the current month (used to back-calculate last month's ending balance) */
  currentPeriodStartDate: Date;
  /** Now (or end of current range) */
  currentPeriodEndDate: Date;
  /** Previous month start */
  previousMonthStartDate: Date;
  /** Previous month end */
  previousMonthEndDate: Date;
};

type TrendDirection = 'increasing' | 'decreasing' | 'default';

const EPSILON = 0.01;

/**
 * Computes runway in months using:
 * - Current runway: current cash balance / previous month's burn (full calendar month)
 * - Previous month runway: last month's closing cash balance / two-months-ago burn (full month)
 */
export const getEstimatedRunwayData = ({
  accounts,
  baseCurrency,
  currentPeriodStartDate,
  currentPeriodEndDate,
  previousMonthStartDate,
  previousMonthEndDate,
}: EstimatedRunwayInput) => {
  // 1) Current cash (as of now)
  const currentCashInBaseCurrency = (accounts || []).reduce((sum, a) => sum + (a.baseCurrencyAvailable || 0), 0);

  // 2) Previous month's burn (full calendar month)
  const prevPrevMonthStart = startOfMonth(subMonths(previousMonthStartDate, 1));
  const prevPrevMonthEnd = endOfMonth(subMonths(previousMonthStartDate, 1));
  const previousMonthBurn = getMonthlyBurnData({
    accounts,
    baseCurrency,
    currentPeriodStartDate: previousMonthStartDate,
    currentPeriodEndDate: previousMonthEndDate,
    previousPeriodStartDate: prevPrevMonthStart,
    previousPeriodEndDate: prevPrevMonthEnd,
  }).monthlyBurnInBaseCurrency;

  // 3) Last month's closing cash balance = current cash - net change since the start of this month
  const allTransactions = (accounts || []).flatMap((account) =>
    (account.transactions || []).map(({ fspDate, baseCurrencyAmount, isCredit }) => ({
      fspDate,
      baseCurrencyAmount: baseCurrencyAmount || 0,
      isCredit: !!isCredit,
    })),
  );

  const txInCurrentRange = allTransactions.filter((t) =>
    t.fspDate
      ? new UTCDate(t.fspDate) >= currentPeriodStartDate && new UTCDate(t.fspDate) <= currentPeriodEndDate
      : false,
  );

  // Net cashflow defined as: subtract credits, add debits
  const netCashFlowInCurrentRange = txInCurrentRange.reduce((acc, t) => {
    const amountAbs = Math.abs(t.baseCurrencyAmount);
    const signedByCreditDebit = t.isCredit ? -amountAbs : amountAbs;
    return acc + signedByCreditDebit;
  }, 0);

  const netChangeInBalance = -netCashFlowInCurrentRange;
  const previousMonthClosingCash = currentCashInBaseCurrency - netChangeInBalance;

  // 4) Two-months-ago burn (full calendar month)
  const twoMonthsAgoStart = startOfMonth(subMonths(previousMonthStartDate, 1));
  const twoMonthsAgoEnd = endOfMonth(subMonths(previousMonthStartDate, 1));
  const beforeTwoMonthsStart = startOfMonth(subMonths(previousMonthStartDate, 2));
  const beforeTwoMonthsEnd = endOfMonth(subMonths(previousMonthStartDate, 2));
  const twoMonthsAgoBurn = getMonthlyBurnData({
    accounts,
    baseCurrency,
    currentPeriodStartDate: twoMonthsAgoStart,
    currentPeriodEndDate: twoMonthsAgoEnd,
    previousPeriodStartDate: beforeTwoMonthsStart,
    previousPeriodEndDate: beforeTwoMonthsEnd,
  }).monthlyBurnInBaseCurrency;

  // 5) Compute runways (months)
  const prevMonthBurnAbs = Math.abs(previousMonthBurn || 0);
  const twoMonthsAgoBurnAbs = Math.abs(twoMonthsAgoBurn || 0);

  const currentRunwayMonths = prevMonthBurnAbs < EPSILON ? Infinity : currentCashInBaseCurrency / prevMonthBurnAbs;
  const previousRunwayMonths =
    twoMonthsAgoBurnAbs < EPSILON ? Infinity : previousMonthClosingCash / twoMonthsAgoBurnAbs;

  // 6) Trend and delta in months
  let deltaMonths = currentRunwayMonths - previousRunwayMonths;
  // Guard against Infinity - Infinity
  if (!Number.isFinite(deltaMonths)) deltaMonths = 0;

  let trend: TrendDirection = 'default';
  if (deltaMonths > EPSILON) trend = 'increasing';
  else if (deltaMonths < -EPSILON) trend = 'decreasing';

  const valueTone =
    trend === 'increasing'
      ? ('positive' as const)
      : trend === 'decreasing'
        ? ('negative' as const)
        : ('neutral' as const);

  const formatMonths = (months: number) => {
    if (!Number.isFinite(months)) return '∞ months';
    if (months < 0) return '0 months';
    return `${months.toFixed(1)} months`;
  };

  const formatDelta = (months: number) => {
    if (!Number.isFinite(months) || Math.abs(months) < EPSILON) return '0.0mo';
    const sign = months > 0 ? '+' : '';
    return `${sign}${months.toFixed(1)}mo`;
  };

  return {
    formattedRunway: formatMonths(currentRunwayMonths),
    percentageChange: formatDelta(deltaMonths),
    trend,
    valueTone,
    // raw values if needed by callers in the future
    currentRunwayInMonths: Number.isFinite(currentRunwayMonths) ? currentRunwayMonths : Infinity,
    previousRunwayInMonths: Number.isFinite(previousRunwayMonths) ? previousRunwayMonths : Infinity,
  };
};
